/**
 * Utility functions for validating form inputs
 */

/**
 * Validates and parses site IDs from a string input
 * @param {string} input - Comma or newline separated site IDs
 * @param {string} type - Type of validation ('manual' or 'demo') for appropriate error messages
 * @returns {Object} - Validation results with parsed siteIds and any errors
 */
export const validateSiteIds = (input, type = 'manual') => {
    // Check if input is empty
    if (!input || !input.trim()) {
        const errorMessage =
            type === 'demo'
                ? 'Please enter at least one demo site ID'
                : 'Please enter at least one site ID';

        return {
            isValid: false,
            error: errorMessage,
            siteIds: [],
        };
    }

    // Parse site IDs (comma or new line separated)
    const siteIds = input
        .split(/[,\n]/)
        .map((id) => id.trim())
        .filter(Boolean);

    // Check for special characters (only allow alphanumeric, hyphens, and underscores)
    const specialCharRegex = /[^a-zA-Z0-9\-_]/;
    const hasSpecialChars = siteIds.some((id) => specialCharRegex.test(id));

    if (hasSpecialChars) {
        return {
            isValid: false,
            error: 'No special characters allowed. Please use only letters, numbers, hyphens and underscores',
            siteIds: [],
        };
    }

    // Check for maximum number of site IDs
    if (siteIds.length > 10) {
        const maxErrorMessage =
            type === 'demo'
                ? 'Please enter maximum 10 demo site IDs'
                : 'Please enter maximum 10 site IDs';

        return {
            isValid: false,
            error: maxErrorMessage,
            siteIds: [],
        };
    }

    // Return valid unique site IDs (remove duplicates)
    const uniqueSiteIds = [...new Set(siteIds)];

    return {
        isValid: true,
        siteIds: uniqueSiteIds,
        error: null,
    };
};
